/**
 * SessionService — Handles clean session initialization on login.
 * 
 * Responsibilities:
 *  1. Clear previous UI navigation state
 *  2. Clear temporary workflow cache
 *  3. Reset screen history stack
 *  4. Reset navigation context
 *  5. Reset active module references
 *  6. Reset unsaved temporary states
 * 
 * Usage:
 *   import SessionService from '@/services/SessionService';
 *   SessionService.resetNavigationState();  // Call on every login
 *   SessionService.clearSessionWorkflowCache();
 *   SessionService.initializeFreshWorkspace();
 */

// Session storage keys to clear on login
const SESSION_KEYS_TO_CLEAR = [
  'hubNavigationState',
  'currentModule',
  'currentSubModule',
  'currentScreen',
  'retailcliq_nav_history',
  'retailcliq_active_screens',
  'retailcliq_form_drafts',
  'retailcliq_temp_workflow',
  'retailcliq_unsaved_state',
  'retailcliq_modal_state',
  'retailcliq_workflow_step',
];

// Local storage keys to preserve (NOT cleared on login)
const PRESERVE_LOCAL_KEYS = [
  'retailcliq_header_theme',
  'sidebarExpanded',
  'retailcliq_help_registry',
  'retailcliq_theme',
];

class _SessionService {
  /**
   * Reset all navigation state — call on every login
   */
  resetNavigationState() {
    try {
      // Clear session storage navigation keys
      SESSION_KEYS_TO_CLEAR.forEach(key => {
        try { 
          sessionStorage.removeItem(key); 
        } catch (e) { 
          console.debug('[SessionService] Failed to clear key:', key, e?.message); 
        }
      });

      // Reset hub navigation to clean state
      const cleanHubState = {
        lastModule: null,
        lastSubModule: null,
        expandedModules: {},
        activeTab: 'apps'
      };
      sessionStorage.setItem('hubNavigationState', JSON.stringify(cleanHubState));

      console.log('[SessionService] Navigation state reset');
    } catch (e) {
      console.warn('[SessionService] Failed to reset navigation:', e);
    }
  }

  /**
   * Clear temporary workflow cache (form drafts, modal states, etc.)
   */
  clearSessionWorkflowCache() {
    try {
      // Clear form drafts
      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('_draft') || key.includes('_temp') || key.includes('_unsaved')) {
          sessionStorage.removeItem(key);
        }
      });

      // Clear any modal/dialog states
      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('modal') || key.includes('dialog') || key.includes('workflow')) {
          sessionStorage.removeItem(key);
        }
      });

      console.log('[SessionService] Workflow cache cleared');
    } catch (e) {
      console.warn('[SessionService] Failed to clear workflow cache:', e);
    }
  }

  /**
   * Clear temporary screen data (incomplete transactions, form state)
   */
  clearTempScreenData() {
    try {
      // Clear screen-specific temp data
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('screen_') || key.startsWith('form_') || key.startsWith('transaction_')) {
          sessionStorage.removeItem(key);
        }
      });

      console.log('[SessionService] Temp screen data cleared');
    } catch (e) {
      console.warn('[SessionService] Failed to clear temp screen data:', e);
    }
  }

  /**
   * Initialize fresh workspace — complete login initialization
   */
  initializeFreshWorkspace() {
    this.resetNavigationState();
    this.clearSessionWorkflowCache();
    this.clearTempScreenData();
    
    // Set fresh login timestamp
    sessionStorage.setItem('retailcliq_login_timestamp', new Date().toISOString());
    
    console.log('[SessionService] Fresh workspace initialized');
  }

  /**
   * Save recent activity for "Resume Work" widget (limited history)
   */
  saveRecentActivity(activity) {
    try {
      const key = 'retailcliq_recent_activity';
      let activities = JSON.parse(localStorage.getItem(key) || '[]');
      
      // Add new activity at the start
      activities.unshift({
        ...activity,
        timestamp: new Date().toISOString()
      });

      // Keep only last 10 activities
      activities = activities.slice(0, 10);
      
      localStorage.setItem(key, JSON.stringify(activities));
    } catch (e) {
      console.warn('[SessionService] Failed to save recent activity:', e);
    }
  }

  /**
   * Get recent activities for "Resume Work" widget
   */
  getRecentActivities() {
    try {
      return JSON.parse(localStorage.getItem('retailcliq_recent_activity') || '[]');
    } catch (e) {
      return [];
    }
  }

  /**
   * Save favorite/pinned modules (persists across sessions)
   */
  savePinnedModules(modules) {
    try {
      localStorage.setItem('retailcliq_pinned_modules', JSON.stringify(modules));
    } catch (e) {
      console.warn('[SessionService] Failed to save pinned modules:', e);
    }
  }

  /**
   * Get pinned modules
   */
  getPinnedModules() {
    try {
      return JSON.parse(localStorage.getItem('retailcliq_pinned_modules') || '[]');
    } catch (e) {
      return [];
    }
  }

  /**
   * Save draft reference (for "Resume Work" — NOT auto-restored)
   */
  saveDraftReference(screenId, draftId, summary) {
    try {
      const key = 'retailcliq_draft_refs';
      let drafts = JSON.parse(localStorage.getItem(key) || '[]');
      
      // Update or add draft reference
      const existing = drafts.findIndex(d => d.screenId === screenId);
      if (existing >= 0) {
        drafts[existing] = { screenId, draftId, summary, timestamp: new Date().toISOString() };
      } else {
        drafts.unshift({ screenId, draftId, summary, timestamp: new Date().toISOString() });
      }

      // Keep only last 5 drafts
      drafts = drafts.slice(0, 5);
      
      localStorage.setItem(key, JSON.stringify(drafts));
    } catch (e) {
      console.warn('[SessionService] Failed to save draft reference:', e);
    }
  }

  /**
   * Get draft references for "Resume Work" widget
   */
  getDraftReferences() {
    try {
      return JSON.parse(localStorage.getItem('retailcliq_draft_refs') || '[]');
    } catch (e) {
      return [];
    }
  }

  /**
   * Clear a specific draft reference
   */
  clearDraftReference(screenId) {
    try {
      const key = 'retailcliq_draft_refs';
      let drafts = JSON.parse(localStorage.getItem(key) || '[]');
      drafts = drafts.filter(d => d.screenId !== screenId);
      localStorage.setItem(key, JSON.stringify(drafts));
    } catch (e) {
      console.warn('[SessionService] Failed to clear draft reference:', e);
    }
  }
}

const SessionService = new _SessionService();
export default SessionService;
